// Copyright (c) 2022 by Move Closer

import isPlainObject from 'lodash/isPlainObject'
import isString from 'lodash/isString'

import { logger } from '../../../support'

import { File, FileType } from '../contracts'

/**
 * Determines whether the passed-in `input` object correctly implements the `File` interface.
 *
 * @typeGuard
 */
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export const isFile = (input: any, type?: FileType): input is File<FileType> => {
  // Validate the type of the input.
  if (!isPlainObject(input)) {
    const message: string = `isFile(): Expected the [input] to be a plain object, but got [${typeof input}]!`
    logger([message, input], 'error')
    return false
  }

  // Validate the presence of the required keys.
  const requiredKeys = ['id', 'mime', 'size', 'type', 'url']
  for (const requiredKey of requiredKeys) {
    if (typeof input[requiredKey] === 'undefined') {
      const message: string = `isFile(): [${requiredKey}] key is missing!`
      logger([message, input], 'error')
      return false
    }
  }

  // Validate the type of the number keys.
  const numberKeys = ['id']
  for (const numberKey of numberKeys) {
    if (!isFinite(input[numberKey])) {
      const message: string = `isFile(): Expected [input.${numberKey}] to be a [number], but got [${typeof input[numberKey]}]!`
      logger([message, input], 'error')
      return false
    }
  }

  // Validate the type of the string keys.
  const stringKeys = ['mime', 'size', 'url']
  for (const stringKey of stringKeys) {
    if (!isString(input[stringKey])) {
      const message: string = `isFile(): Expected [input.${stringKey}] to be a [string], but got [${typeof input[stringKey]}]!`
      logger([message, input], 'error')
      return false
    }

    if (input[stringKey].length === 0) {
      const message: string = `isFile(): Value of the [input.${stringKey}] can NOT be empty!`
      logger([message, input], 'error')
      return false
    }
  }

  // Validate the value of the `type` key.
  if (typeof type !== 'undefined') {
    if (input.type !== type) {
      const message: string = `isFile(): Expected the value of the [input.type] to be exactly equal to the [${type}], but got [${input.type}]!`
      logger([message, input], 'error')
      return false
    }
  } else {
    const validFileTypes = Object.values(FileType)
    const hasValidFileType: boolean = validFileTypes.reduce<boolean>((matchAlreadyFound, curr) => (
      matchAlreadyFound ? true : input.type === curr
    ), false)
    if (!hasValidFileType) {
      const message: string = `isFile(): Invalid value of the [input.type]! Valid values are [${validFileTypes.join(', ')}], but got [${input.type}]!`
      logger([message, input], 'error')
      return false
    }
  }

  return true
}
